import type { TFunction } from "i18next";
import { MissingBaseline, SavingsList, SavingsMeasure, WithoutSavingReason } from "../../autogen/bff-api";
import { displaySum } from "../../common/formatting/displaySum";
import { formatNumber } from "../../common/formatting/formatNumber";

export const LIST_PARAM = "list";

export const MEASURE_PARAM = "measure";

export const parseSavingsMeasure = (value: string | null): SavingsMeasure =>
  value === SavingsMeasure.FirstBid ? SavingsMeasure.FirstBid : SavingsMeasure.EstimatedValue;

export const formatShare = (share: number): string => `${formatNumber(Math.round(Math.abs(share) * 1000) / 10)} %`;

export const formatMoney = (value: number, currency: string): string =>
  displaySum({ value: Math.round(value), currency });

export const SAVINGS_ORDER: SavingsList[] = [
  SavingsList.BelowEstimate,
  SavingsList.Negotiation,
  SavingsList.AboveEstimate,
  SavingsList.WithoutSaving,
  SavingsList.WithoutBaseline,
];

export const parseSavingsList = (value: string | null): SavingsList | null =>
  SAVINGS_ORDER.find((list) => list === value) ?? null;

export const savingsLabel = (t: TFunction, list: SavingsList): string =>
  ({
    [SavingsList.BelowEstimate]: t("Below estimated value"),
    [SavingsList.Negotiation]: t("Saved in negotiation"),
    [SavingsList.AboveEstimate]: t("Above estimated value"),
    [SavingsList.WithoutSaving]: t("No savings"),
    [SavingsList.WithoutBaseline]: t("No baseline"),
  })[list];

export const savingsDefinition = (t: TFunction, list: SavingsList): string =>
  ({
    [SavingsList.BelowEstimate]: t(
      "Estimated value minus the value at award, for the procurements awarded in the period that came in below their estimate. The percentage is of their estimated value. A procurement counts once, however many contracts it gave."
    ),
    [SavingsList.Negotiation]: t(
      "The supplier's first bid minus its bid in the last negotiation round, as recorded at award, for the procurements awarded in the period. The percentage is of the first bids. It is never added to «Below estimated value»."
    ),
    [SavingsList.AboveEstimate]: t(
      "Procurements awarded in the period whose value at award was higher than the estimate, and by how much. They are not subtracted from «Below estimated value»."
    ),
    [SavingsList.WithoutSaving]: t(
      "Contracts awarded at or above their estimated value, and contracts whose price did not go down in negotiation. A contract can still have a saving against the other measure."
    ),
    [SavingsList.WithoutBaseline]: t(
      "Contracts with nothing to measure a saving against: registered without a procurement, from a procurement you have no access to, from a procurement without an estimated value or a value at award, from one awarded at a negative value, or in another currency than NOK. A contract without a procurement counts in the period of its award date, or of the day it was registered."
    ),
  })[list];

export const missingBaselineLabel = (t: TFunction, reason: MissingBaseline): string =>
  ({
    [MissingBaseline.NoProcurement]: t("Registered without a procurement"),
    [MissingBaseline.ProcurementNotVisible]: t("No access to the procurement"),
    [MissingBaseline.NotAwarded]: t("The procurement is not awarded"),
    [MissingBaseline.NoEstimatedValue]: t("The procurement has no estimated value"),
    [MissingBaseline.NoAwardValue]: t("The procurement has no value at award"),
    [MissingBaseline.NegativeAwardValue]: t("Negative value at award"),
    [MissingBaseline.OtherCurrency]: t("Not in NOK"),
  })[reason];

export const withoutSavingLabel = (t: TFunction, reason: WithoutSavingReason): string =>
  ({
    [WithoutSavingReason.AboveEstimate]: t("Above estimated value"),
    [WithoutSavingReason.AtEstimate]: t("At estimated value"),
    [WithoutSavingReason.UnchangedInNegotiation]: t("Unchanged in negotiation"),
    [WithoutSavingReason.AboveFirstBid]: t("Above the first bid"),
  })[reason];
